import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BusinessServiceDocumentModel } from 'src/app/modules/shared/models/document.model';
import { BusinessServiceAddModel, BusinessServiceUpdateModel, CreateTradieEntitieModel, RegisterBusiness, TradieUpdateLogo } from '../../models/tradie/tradie.model';
import { CreateAnswerModel, UpdateAnswerModel } from '../../models/aditional-preferences/aditional-preferences.model';
import { SWMS } from 'src/app/modules/onboarding/shared/models/SWMS/swms.model';

@Injectable({
  providedIn: 'root'
})
export class TradieService {

  private endpoint = environment.endpointURL + '/api/Tradie';
  private endpointPaidNow = environment.endpointURL + '/api/PaidNow';

  constructor( private http: HttpClient ) { }

  // Get verified tradie by id
  public getTradieById(tradeId: string): Observable<any> {
    const url = `${this.endpoint}?id=${tradeId}`;
    return this.http.get<any>(url);
  }

  // Update Business Info
  public updateBusinessInfo(data: any): Observable<any> {
    const url = `${this.endpoint}/UpdateBusinessInfo`;
    return this.http.put<any>(url, data);
  }
  // Update Business Address
  public updateBusinessAddress(data: any): Observable<any> {
    const url = `${this.endpoint}/UpdateAddress`;
    return this.http.put<any>(url, data);
  }
  // Update Business Contact Details
  public updateBusinessContact(data: any): Observable<any> {
    const url = `${this.endpoint}/UpdateContact`;
    return this.http.put<any>(url, data);
  }

  // Update Business Contact Name
  public updateBusinessContactName(data: any): Observable<any> {
    const url = `${this.endpoint}/UpdateContactName`;
    return this.http.put<any>(url, data);
  }

  // Update Business Contact Mobile
  public updateBusinessContactMobile(data: any): Observable<any> {
    const url = `${this.endpoint}/UpdateContactMobile`;
    return this.http.put<any>(url, data);
  }
  
  public updateOrientationStatus(data: any) {
    const url = `${this.endpoint}/UpdateOrientationStatus`;
    return this.http.patch<any>(url, data);
  }
  public updateIsDisclaimerStatus(data: any) {
    const url = `${this.endpoint}/UpdateIsDisclaimerStatus`;
    return this.http.patch<any>(url, data);
  }

  public getOnboardingProgress(tradeId: string): Observable<any> {
    const url = `${this.endpoint}/GetOnboardingProgress?id=${tradeId}`;
    return this.http.get<any>(url);
  }

  public getBusinessServiceStatus(id: string): Observable<any> {
    const url = `${this.endpoint}/GetBusinessServiceStatus?id=${id}`;
    return this.http.get<any>(url);
  }

  public registerBusiness(data: RegisterBusiness): Observable<any> {
    const url = `${this.endpoint}/RegisterBusiness`;
    return this.http.post<any>(url, data);
  }

  public getAbnInfo(abn: string): Observable<any> {
    const url = `${this.endpoint}/AbnInfo?abn=${abn}`;
    return this.http.get<any>(url);
  }

  public checkIsExistsABN(abn: string): Observable<any> {
    const url = `${this.endpoint}/IsExistsABN?abn=${abn}`;
    return this.http.get<any>(url);
  }

  // --- BUSINESS SERVICE DOCUMENT --- //
  public addBusinessServiceDocument(data: BusinessServiceDocumentModel): Observable<any> {
    const url = `${this.endpoint}/BusinessServiceDocument`;
    return this.http.post<any>(url, data);
  }

  public updateBusinessServiceDocument(data: BusinessServiceDocumentModel): Observable<any> {
    const url = `${this.endpoint}/BusinessServiceDocument`;
    return this.http.put<any>(url, data);
  }

  public deleteBusinessServiceDocument(id: string): Observable<any> {
    const url = `${this.endpoint}/BusinessServiceDocument?id=${id}`;
    return this.http.delete<any>(url);
  }

  public getBusinessServiceDocumentLink(businessInfoId: string, serviceCategoryId: string): Observable<any> {
    const url = `${this.endpoint}/GetBusinessServiceDocumentLink?businessInfoId=${businessInfoId}&serviceCategoryId=${serviceCategoryId}`;
    return this.http.get<any>(url);
  }

  // --- Trade Profile --- //
  public updateLogo(data: TradieUpdateLogo): Observable<any> {
    const url = `${this.endpoint}/UpdateLogo`;
    return this.http.patch<any>(url, data);
  }

  // --- Business Services --- //
  public addBusinessService(data: BusinessServiceAddModel): Observable<any> {
    const url = `${this.endpoint}/BusinessService`;
    return this.http.post<any>(url, data);
  }

  public updateBusinessService(data: BusinessServiceUpdateModel): Observable<any> {
    const url = `${this.endpoint}/BusinessService`;
    return this.http.put<any>(url, data);
  }

  public verifyBusinessService(id: string, isVerified: boolean): Observable<any> {
    const url = `${this.endpoint}/VerifyBusinessService?id=${id}&isVerified=${isVerified}`;
    return this.http.patch<any>(url, id);
  }

  public deleteBusinessService(id: string): Observable<any> {
    const url = `${this.endpoint}/BusinessService?id=${id}`;
    return this.http.delete<any>(url);
  }

  // --- Professional Idemnity Compliance -- //
  public createProfessionalIdemnityCompliance(data: any): Observable<any> {
    const url = `${this.endpoint}/ProfessionalIdemnityCompliance`;
    return this.http.post<any>(url, data);
  }

  public updateProfessionalIdemnityCompliance(data: any): Observable<any> {
    const url = `${this.endpoint}/ProfessionalIdemnityCompliance`;
    return this.http.put<any>(url, data);
  }

  public getProfessionalIdemnityCompliance(businessInfoId: string): Observable<any> {
    const url = `${this.endpoint}/ProfessionalIdemnityCompliance?businessInfoId=${businessInfoId}`;
    return this.http.get<any>(url);
  }
  
  // --- Health Safety Compliance -- //
  public createHealthSafetyCompliance(data: any): Observable<any> {
    const url = `${this.endpoint}/HealthSafetyCompliance`;
    return this.http.post<any>(url, data);
  }

  public updateHealthSafetyCompliance(data: any): Observable<any> {
    const url = `${this.endpoint}/HealthSafetyCompliance`;
    return this.http.put<any>(url, data);
  }

  public getHealthSafetyCompliance(businessInfoId: string): Observable<any> {
    const url = `${this.endpoint}/HealthSafetyCompliance?businessInfoId=${businessInfoId}`;
    return this.http.get<any>(url);
  }

  public deleteHealthSafetyCompliance(id: string, businessInfoId: string, isArchived: boolean, userId : string ): Observable<any> {
    const url = `${this.endpoint}/HealthSafetyCompliance?id=${id}&businessInfoId=${businessInfoId}&isArchived=${isArchived}&UserId=${userId}`;
    return this.http.delete<any>(url);
  }

  // --- SWMS --- // 
  public createSWMS(data: SWMS): Observable<any> {
    const url = `${this.endpoint}/SWMS`;
    return this.http.post<any>(url, data);
  }
  public updateSWMS(data: SWMS): Observable<any> {
    const url = `${this.endpoint}/SWMS`;
    return this.http.put<any>(url, data);
  }
  public getSWMSList(businessInfoId: string): Observable<any> {
    const url = `${this.endpoint}/SWMS?businessInfoId=${businessInfoId}`;
    return this.http.get<any>(url);
  }

  // --- Aditional Preference --- //
  public getAditionalPreference(businessInfoId: string): Observable<any> {
    const url = `${this.endpoint}/AditionalPreference?businessInfoId=${businessInfoId}`;
    return this.http.get<any>(url);
  }

  public createAditionalPreference(data: CreateAnswerModel): Observable<any> {
    const url = `${this.endpoint}/AditionalPreference`;
    return this.http.post<any>(url, data);
  }

  public updateAditionalPreference(data: UpdateAnswerModel): Observable<any> {
    const url = `${this.endpoint}/AditionalPreference`;
    return this.http.put<any>(url, data);
  }

  public deleteAditionalPreference(answerId: string): Observable<any> {
    const url = `${this.endpoint}/AditionalPreference?id=${answerId}`;
    return this.http.delete<any>(url);
  }

  public getGeneralComplianceStatus(id: string): Observable<any> {
    const url = `${this.endpoint}/GeneralComplianceStatus?businessInfoId=${id}`; 
    return this.http.get<any>(url);
  }

  public getComplianceStatus(businessInfoId: string, generalComplianceType: number): Observable<any> {
    const url = `${this.endpoint}/ComplianceStatus?businessInfoId=${businessInfoId}&generalComplianceType=${generalComplianceType}`; 
    return this.http.get<any>(url);
  }

  // SET Trade Entity When Onboarding
  public createTradieEntitie(data: CreateTradieEntitieModel): Observable<any> {
    const url = `${this.endpoint}/CreateTradieEntitie`;
    return this.http.post<any>(url, data);
  }

  // Team membr onboarding completed
  public createTeamMemberConditionAcceptance(data: any) {
    const url = `${this.endpoint}/CreateTeamMemberConditionAcceptance`;
    return this.http.post<any>(url, data);
  }

  public getTradieSubscriptionDetailsById(userId: string, businessInfoId:string): Observable<any> {
    const url = `${this.endpoint}/GetTradieSubscriptionDetails?userId=${userId}&businessInfoId=${businessInfoId}`;
    return this.http.get<any>(url);
  }

  public cancelTradieSubscriptions(data: any) {
    const url = `${this.endpoint}/CancelTradieSubscriptions`;
    return this.http.patch<any>(url, data);
  }

  // Create Subscription for NSW
  public CreateTradieSubscriptions(data: any) {
    const url = `${this.endpoint}/CreateTradieSubscriptions`;
    return this.http.post<any>(url, data);
  }

  public getOnbordingDate(businessInfoId : string): Observable<any> {
    const url = `${this.endpoint}/GetOnbordingDateByBusinessInfoId?businessInfoId=${businessInfoId}`;
    return this.http.get<any>(url);
  }

  // Paidnow
  public agreeToPaidNowByBusinessInfoId(businessInfoId: string, isPaidNow: boolean): Observable<any> {
    let data = {
      "businessInfoId": businessInfoId,
      "isPaidNow": isPaidNow
    }
    // const url = `${this.endpoint}/Ndis/UpdateHasNdisStatus?businessInfoId=${businessInfoId}&hasNdis=${hasNdis}`;
    const url = `${this.endpointPaidNow}/CreatePaidNow`;
    return this.http.post<any>(url, data);
  }

  public GetPaidNowStatusByBusinessInfoId(businessInfoId: string): Observable<any> {
    const url = `${this.endpointPaidNow}/GetPaidNowByBusinessInfoId?BusinessInfoId=${businessInfoId}`;
    return this.http.get<any>(url);
  }
}